"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Lock, Database, Truck, UserCheck, Mail, MapPin, Phone, FileText, Clock, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const policySections = [{
  id: "info-collected",
  number: "01",
  title: "Information collected",
  description: "Data captured during procurement, account registration, and workshop equipment checkout.",
  icon: Database,
  items: [{
    label: "Account credentials",
    detail: "Full name, business or workshop name, billing and delivery addresses, authorized phone number, and verified email address."
  }, {
    label: "Commercial transactions",
    detail: "Hardware orders, itemized invoice records, payment confirmations, and applied tax exemption documentation."
  }, {
    label: "Technical & device identifiers",
    detail: "Depot portal IP addresses, browser specifications, operating environment telemetry, and session navigation paths."
  }, {
    label: "Support diagnostics",
    detail: "Equipment support logs, warranty claim details, maintenance gear serial registrations, and technician correspondence."
  }]
}, {
  id: "data-usage",
  number: "02",
  title: "How data is used",
  description: "Direct operational application of collected records to fulfill orders and sustain depot operations.",
  icon: ShieldCheck,
  items: [{
    label: "Order fulfillment",
    detail: "Processing workshop gear purchases, dispatching freight carriers, and routing real-time shipping manifests."
  }, {
    label: "Warranty & maintenance tracking",
    detail: "Validating tier coverage, serial number authentication, and issuing scheduled maintenance alerts."
  }, {
    label: "Inventory forecasting",
    detail: "Evaluating tool demand to guarantee rapid fulfillment across our 11 workshop and garage equipment categories."
  }, {
    label: "Account security",
    detail: "Preventing unauthorized commercial transactions, mitigating fraud risks, and auditing secure checkout sessions."
  }]
}, {
  id: "security-protocols",
  number: "03",
  title: "Storage & security protocols",
  description: "Standardized encryption layers and retention baselines safeguarding industrial transactional records.",
  icon: Lock,
  items: [{
    label: "Transport layer security",
    detail: "All data transfers use TLS 1.3 encryption with 256-bit AES protocols across web and database nodes."
  }, {
    label: "Payment isolation",
    detail: "Credit card and banking telemetry are processed via PCI-DSS Level 1 certified gateways; raw card data is never stored locally."
  }, {
    label: "Retention cycles",
    detail: "Active purchase records remain accessible for seven fiscal years to comply with federal tax and warranty mandates."
  }, {
    label: "Access authorization",
    detail: "Internal depot staff operate under role-based access control (RBAC) with enforced multi-factor authentication."
  }]
}, {
  id: "third-party-sharing",
  number: "04",
  title: "Third-party sharing & processors",
  description: "Strictly bounded external disclosures strictly required to execute logistics and financial settlement.",
  icon: Truck,
  items: [{
    label: "Freight & courier partners",
    detail: "Address and contact details provided exclusively to certified carriers (e.g., freight dispatchers, parcel services) for delivery."
  }, {
    label: "Financial processors",
    detail: "Encrypted transmission to authorized clearing banks and payment processing gateways for settlement verification."
  }, {
    label: "Regulatory compliance",
    detail: "Disclosures provided solely when required by enforceable judicial order, federal subpoena, or statutory tax audit."
  }, {
    label: "No commercial brokerage",
    detail: "Huga Scalet does not sell, lease, or monetize customer records, tool inquiries, or buyer registries to data brokers."
  }]
}, {
  id: "user-rights",
  number: "05",
  title: "User rights & data requests",
  description: "Statutory rights available to registered workshop operators, commercial purchasers, and private account holders.",
  icon: UserCheck,
  items: [{
    label: "Record inspection",
    detail: "Request a complete digital extract of all personal telemetry, order logs, and warranty records tied to your profile."
  }, {
    label: "Rectification of details",
    detail: "Update inaccurate business registrations, address designations, or billing metadata through our depot desk."
  }, {
    label: "Account erasure",
    detail: "Request deletion of non-essential profile data, subject to non-waivable tax and commercial warranty record requirements."
  }, {
    label: "Consent revocation",
    detail: "Opt out of non-essential operational bulletins, parts catalogs, or stock replenishment notifications instantly."
  }]
}];
export default function Privacy() {
  const prefersReducedMotion = useReducedMotion();
  const motionProps = prefersReducedMotion ? {} : {
    initial: {
      opacity: 0,
      y: 14
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.15
    },
    transition: {
      duration: 0.24,
      ease: [0.16, 1, 0.3, 1]
    }
  };
  return <section id="privacy-policy" data-nav="dark" className="bg-background text-foreground py-12 md:py-20 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        {/* Section Header */}
        <motion.div {...motionProps} className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <Badge variant="outline" className="bg-muted/60 text-muted-foreground border-border font-mono text-xs px-2.5 py-1 tracking-wider uppercase">
              <FileText className="w-3.5 h-3.5 mr-1 text-primary" />
              <span>SPEC DOC // HS-POL-04</span>
            </Badge>
            <Badge variant="outline" className="bg-muted/60 text-muted-foreground border-border font-mono text-xs px-2.5 py-1 tracking-wider uppercase">
              <Clock className="w-3.5 h-3.5 mr-1 text-muted-foreground" />
              <span>REV: 2025.1</span>
            </Badge>
          </div>

          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-1.5 h-8 bg-primary self-center" />
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold uppercase tracking-tight text-foreground">
              Privacy Policy
            </h1>
          </div>

          <p className="font-sans text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg">
            Huga Scalet LLC operates with transparent data management standards. This document outlines how commercial records, telemetry, and transactional details are collected, protected, and processed.
          </p>
        </motion.div>

        {/* Quick Spec Matrix Bar */}
        <motion.div {...motionProps} className="mb-10 bg-card border border-border p-4 sm:p-5 flex flex-wrap items-center justify-between gap-4 text-xs font-mono text-muted-foreground">
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-primary" />
            <span>APPLICABILITY: U.S. COMMERCIAL &amp; RETAIL ORDERS</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-chart-2" />
            <span>ENCRYPTION: 256-BIT AES / TLS 1.3</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-chart-3" />
            <span>DATA BROKERAGE: STRICTLY ZERO</span>
          </div>
        </motion.div>

        {/* Policy Content Blocks */}
        <div className="space-y-6 md:space-y-8">
          {policySections.map((section, i) => {
          const SectionIcon = section.icon;
          return <motion.div key={section.id} data-index={i} {...motionProps} transition={prefersReducedMotion ? {} : {
            duration: 0.24,
            delay: i * 0.04,
            ease: [0.16, 1, 0.3, 1]
          }}>
                <Card className="bg-card border-border shadow-sm">
                  <CardHeader className="border-b border-border pb-4 bg-muted/20">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-xs font-bold text-primary px-2 py-1 bg-primary/10 border border-primary/20">
                          {section.number}
                        </span>
                        <div className="flex items-center gap-2">
                          <SectionIcon className="w-5 h-5 text-primary" />
                          <CardTitle className="text-xl sm:text-2xl font-serif font-bold uppercase tracking-tight text-card-foreground">
                            {section.title}
                          </CardTitle>
                        </div>
                      </div>
                      <span className="font-mono text-xs text-muted-foreground">
                        SEC-{section.number} // POLICY
                      </span>
                    </div>
                    <p className="font-sans text-xs sm:text-sm text-muted-foreground mt-1">
                      {section.description}
                    </p>
                  </CardHeader>

                  <CardContent className="pt-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      {section.items.map((item, itemIdx) => <div key={item.label} data-index={itemIdx} className="bg-muted/30 border border-border p-3.5 sm:p-4 flex flex-col justify-start">
                          <div className="flex items-center gap-2 mb-1.5">
                            <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0" />
                            <h4 className="font-sans font-semibold text-sm sm:text-base text-foreground">
                              {item.label}
                            </h4>
                          </div>
                          <p className="font-sans text-xs sm:text-sm text-muted-foreground leading-relaxed pl-5.5">
                            {item.detail}
                          </p>
                        </div>)}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}

          {/* Section 06: Contact & Inquiries */}
          <motion.div {...motionProps}>
            <Card className="bg-card border-border shadow-sm">
              <CardHeader className="border-b border-border pb-4 bg-muted/20">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs font-bold text-primary px-2 py-1 bg-primary/10 border border-primary/20">
                      06
                    </span>
                    <div className="flex items-center gap-2">
                      <Mail className="w-5 h-5 text-primary" />
                      <CardTitle className="text-xl sm:text-2xl font-serif font-bold uppercase tracking-tight text-card-foreground">
                        Privacy contact &amp; compliance desk
                      </CardTitle>
                    </div>
                  </div>
                  <span className="font-mono text-xs text-muted-foreground">
                    SEC-06 // DIRECT INQUIRY
                  </span>
                </div>
                <p className="font-sans text-xs sm:text-sm text-muted-foreground mt-1">
                  Direct channels for data access requests, rectification submissions, and statutory privacy inquiries.
                </p>
              </CardHeader>

              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Physical Address */}
                  <div className="bg-muted/30 border border-border p-4 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="w-4 h-4 text-primary" />
                        <span className="font-mono text-xs font-bold text-foreground uppercase tracking-wider">
                          Depot Address
                        </span>
                      </div>
                      <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                        Huga Scalet LLC
                        <br />
                        2000 W Palmyra Ave, Ste 28
                        <br />
                        Orange, CA 92868, USA
                      </p>
                    </div>
                    <span className="font-mono text-2xs text-muted-foreground mt-4 pt-2 border-t border-border/60">
                      FACILITY // REGIONAL DEPOT
                    </span>
                  </div>

                  {/* Email Support */}
                  <div className="bg-muted/30 border border-border p-4 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Mail className="w-4 h-4 text-primary" />
                        <span className="font-mono text-xs font-bold text-foreground uppercase tracking-wider">
                          Direct Electronic Desk
                        </span>
                      </div>
                      <p className="font-sans text-sm text-muted-foreground leading-relaxed mb-3">
                        Submit formal data export or removal requests to our designated privacy officer.
                      </p>
                    </div>
                    <a href="mailto:HugaScalet@proton.me" className="font-mono text-xs sm:text-sm font-semibold text-primary hover:underline break-all">
                      HugaScalet@proton.me
                    </a>
                  </div>

                  {/* Phone Support */}
                  <div className="bg-muted/30 border border-border p-4 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Phone className="w-4 h-4 text-primary" />
                        <span className="font-mono text-xs font-bold text-foreground uppercase tracking-wider">
                          Voice Assistance
                        </span>
                      </div>
                      <p className="font-sans text-sm text-muted-foreground leading-relaxed mb-3">
                        Monday through Friday, 8:00 AM to 5:00 PM Pacific Standard Time.
                      </p>
                    </div>
                    <a href="tel:+14842918653" className="font-mono text-xs sm:text-sm font-semibold text-primary hover:underline">
                      +1 (484) 291-8653
                    </a>
                  </div>
                </div>

                <Separator className="my-6" />

                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <p className="font-sans text-xs text-muted-foreground">
                    Inquiries are processed in the order received. Standard turnaround window is 30 calendar days for statutory requests.
                  </p>
                  <Button asChild variant="default" className="w-full sm:w-auto font-serif font-bold uppercase tracking-wider text-xs">
                    <a href="mailto:HugaScalet@proton.me?subject=Privacy%20Data%20Request">
                      <Mail className="w-3.5 h-3.5 mr-1" />
                      Submit Data Request
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}